import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router';
import { formatEuro } from '@brimo/shared';
import { clsx } from '../../../ds/clsx';
import {
  Banner,
  Button,
  Card,
  Pill,
  Skeleton,
  Table,
  TableSkeleton,
  useToast,
  type TableColumn,
} from '../../../ds/components';
import { useIsMobile } from '../../../ds/hooks';
import { ApiError, errorMessage } from '../../../lib/api';
import { downloadCsv, useInvalidateDeals, useInvoices, usePipeline } from '../api';
import { DealsTabs, type DealsTab } from '../DealsTabs';
import type { InvoiceRow, PipelineInfo } from '../types';
import { CancelInvoiceDialog, IssueInvoiceDialog, ReceivedDialog } from './InvoiceDialogs';
import pageStyles from '../DealsPage.module.css';
import styles from './invoicing.module.css';

/**
 * Controlling 06/08 „Abrechnung": the four tiles, the invoice table with its row actions, the CSV
 * export and the empty card „Noch nichts abzurechnen.".
 */

export interface InvoicingTabProps {
  onTabChange: (tab: DealsTab) => void;
  pipelineId: string | null;
}

type DialogState =
  | { kind: 'issue'; row: InvoiceRow | null }
  | { kind: 'received'; row: InvoiceRow }
  | { kind: 'cancel'; row: InvoiceRow };

function handoverOf(
  info: PipelineInfo | undefined,
): { stageName: string; responsibleLabel: string | null } | null {
  if (!info || info.locked) return null;
  const stage =
    info.stages.find((entry) => entry.isAfterSalesHandover) ?? info.stages[info.stages.length - 1];
  return stage ? { stageName: stage.name, responsibleLabel: stage.responsibleLabel } : null;
}

export function InvoicingTab({ onTabChange, pipelineId }: InvoicingTabProps) {
  const navigate = useNavigate();
  const mobile = useIsMobile();
  const { toast } = useToast();
  const invalidate = useInvalidateDeals();
  const invoices = useInvoices(true);
  const pipeline = usePipeline(pipelineId, true);
  const [dialog, setDialog] = useState<DialogState | null>(null);

  const rows = invoices.data?.items ?? [];
  const dueRows = useMemo(() => rows.filter((row) => row.status === 'due'), [rows]);
  const forbidden = invoices.error instanceof ApiError && invoices.error.status === 403;

  const columns = useMemo<TableColumn<InvoiceRow>[]>(
    () => [
      {
        id: 'deal',
        header: 'Deal',
        track: 'minmax(0,1.4fr)',
        cell: (row) => <span className={clsx(styles.cell, styles.cellStrong)}>{row.dealLabel}</span>,
      },
      {
        id: 'developer',
        header: 'Bauträger',
        track: 'minmax(0,1fr)',
        cell: (row) => (
          <span className={clsx(styles.cell, styles.cellMuted)}>{row.developerName ?? '—'}</span>
        ),
      },
      {
        id: 'price',
        header: 'Kaufpreis',
        track: '120px',
        numeric: true,
        cell: (row) => (
          <span className={styles.numeric}>{row.priceCents === null ? '—' : formatEuro(row.priceCents)}</span>
        ),
      },
      {
        id: 'commission',
        header: 'Provision',
        track: '110px',
        numeric: true,
        cell: (row) => <span className={styles.numeric}>{formatEuro(row.commissionCents)}</span>,
      },
      {
        id: 'status',
        header: 'Status',
        track: '130px',
        cell: (row) => <Pill variant={row.tone}>{row.statusLabel}</Pill>,
      },
      {
        id: 'state',
        header: 'Stand',
        track: 'minmax(0,1fr)',
        cell: (row) => <span className={clsx(styles.cell, styles.cellMuted)}>{row.stateLine}</span>,
      },
      {
        id: 'actions',
        header: '',
        track: '236px',
        align: 'end',
        cell: (row) => (
          <span className={styles.rowActions} onClick={(event) => event.stopPropagation()}>
            {row.status === 'due' ? (
              <Button size="small" variant="secondary" onClick={() => setDialog({ kind: 'issue', row })}>
                Rechnung stellen
              </Button>
            ) : null}
            {row.status === 'issued' || row.status === 'open' ? (
              <Button size="small" variant="secondary" onClick={() => setDialog({ kind: 'received', row })}>
                Eingang erfassen
              </Button>
            ) : null}
            {row.status === 'due' || row.status === 'issued' || row.status === 'open' ? (
              <Button size="small" variant="ghost" onClick={() => setDialog({ kind: 'cancel', row })}>
                Stornieren
              </Button>
            ) : null}
          </span>
        ),
      },
    ],
    [],
  );

  function finish(message: string) {
    setDialog(null);
    void invalidate();
    toast({ message });
  }

  const showTable = invoices.data && invoices.data.empty === null;

  return (
    <div className={pageStyles.page} style={{ padding: mobile ? undefined : '24px 32px 32px', gap: 18 }}>
      <div className={pageStyles.listHead}>
        <div className={pageStyles.listHeadText}>
          <div className={pageStyles.crumb}>Deals</div>
          <h1 className={pageStyles.listTitle}>Abrechnung</h1>
          <div className={pageStyles.listCount}>Provision gegenüber Bauträgern</div>
        </div>
        {showTable ? (
          <div className={pageStyles.headRight}>
            <Button variant="secondary" onClick={() => downloadCsv('/deals/invoices/export')}>
              Export
            </Button>
            <Button onClick={() => setDialog({ kind: 'issue', row: null })} disabled={dueRows.length === 0}>
              Rechnung stellen
            </Button>
          </div>
        ) : null}
      </div>
      <DealsTabs value="invoicing" onChange={onTabChange} />

      {invoices.isPending ? (
        <>
          <div className={styles.tiles}>
            {[1, 2, 3, 4].map((index) => (
              <div key={index} className={styles.tile}>
                <Skeleton width={60} height={9} />
                <div className={styles.tileValue}>
                  <Skeleton width={96} height={22} />
                </div>
                <div className={styles.tileSub}>
                  <Skeleton width={54} height={10} />
                </div>
              </div>
            ))}
          </div>
          <Card flush className={styles.tableCard}>
            <TableSkeleton
              columns={['minmax(0,1.4fr)', 'minmax(0,1fr)', '120px', '110px', '130px', 'minmax(0,1fr)']}
              rows={5}
              headerTone="plain"
            />
          </Card>
        </>
      ) : null}

      {invoices.isError ? (
        forbidden ? (
          <Banner tone="info">{invoices.error.message}</Banner>
        ) : (
          <Banner
            tone="danger"
            actions={
              <Button variant="secondary" size="small" onClick={() => void invoices.refetch()}>
                Erneut laden
              </Button>
            }
          >
            {errorMessage(invoices.error)}
          </Banner>
        )
      ) : null}

      {invoices.data?.empty ? (
        <div className={styles.empty}>
          <div className={styles.emptyEyebrow}>Abrechnung</div>
          <div className={styles.emptyTitle}>{invoices.data.empty.title}</div>
          <p className={styles.emptyText}>{invoices.data.empty.text}</p>
          <div className={styles.emptyActions}>
            <Button variant="secondary" onClick={() => onTabChange('pipeline')}>
              Zur Pipeline
            </Button>
          </div>
        </div>
      ) : null}

      {showTable && invoices.data ? (
        <>
          <div className={styles.tiles}>
            <div className={styles.tile}>
              <div className={styles.emptyEyebrow}>Fällig</div>
              <div className={styles.tileValue}>{formatEuro(invoices.data.tiles.due.cents)}</div>
              <div className={styles.tileSub}>{invoices.data.tiles.due.sub}</div>
            </div>
            <div className={styles.tile}>
              <div className={styles.emptyEyebrow}>Gestellt</div>
              <div className={styles.tileValue}>{formatEuro(invoices.data.tiles.issued.cents)}</div>
              <div className={styles.tileSub}>{invoices.data.tiles.issued.sub}</div>
            </div>
            <div className={styles.tile}>
              <div className={styles.emptyEyebrow}>Zahlung offen</div>
              <div className={styles.tileValue}>{formatEuro(invoices.data.tiles.open.cents)}</div>
              <div className={styles.tileSub}>{invoices.data.tiles.open.sub}</div>
            </div>
            <div className={styles.tile}>
              <div className={styles.emptyEyebrow}>
                Eingegangen · {invoices.data.tiles.receivedThisMonth.monthLabel}
              </div>
              <div className={styles.tileValue}>
                {formatEuro(invoices.data.tiles.receivedThisMonth.cents)}
              </div>
              <div className={styles.tileSub}>{invoices.data.tiles.receivedThisMonth.sub}</div>
            </div>
          </div>
          <Card flush className={styles.tableCard}>
            <Table
              label="Abrechnung"
              columns={columns}
              rows={rows}
              rowKey={(row) => row.id}
              headerTone="plain"
              onRowClick={(row) => void navigate(`/deals/${row.dealId}?tab=commission`)}
              emptyText="Keine Rechnungen."
            />
          </Card>
        </>
      ) : null}

      <IssueInvoiceDialog
        open={dialog?.kind === 'issue'}
        candidates={dueRows}
        preselectedId={dialog?.kind === 'issue' ? (dialog.row?.id ?? null) : null}
        onClose={() => setDialog(null)}
        onDone={(row) => finish(`Rechnung ${row.invoiceNumber ?? ''} gestellt.`)}
      />
      <ReceivedDialog
        open={dialog?.kind === 'received'}
        row={dialog?.kind === 'received' ? dialog.row : null}
        responsibleLabel={handoverOf(pipeline.data)?.responsibleLabel ?? null}
        onClose={() => setDialog(null)}
        onDone={(result) => finish(`Übergeben an ${result.ownerName}.`)}
      />
      <CancelInvoiceDialog
        open={dialog?.kind === 'cancel'}
        row={dialog?.kind === 'cancel' ? dialog.row : null}
        onClose={() => setDialog(null)}
        onDone={(row) => finish(`Rechnung storniert: ${row.dealLabel}.`)}
      />
    </div>
  );
}
